import {
  generateCacheKey,
  getRedisClient,
  scopeKeyToEnvironment,
} from "./redis-cache";

import { CacheEntities } from "./cache-types";
import { validateCacheKey } from "./redis-cache-utils";

export const FLAGGED_IDS_DEFAULT_TTL_SECONDS = 60 * 60;

type CachedFlag = boolean | null;

/**
 * Answers "which of these ids carry the flag?" with one Redis read for the whole
 * list and one origin call for only the ids Redis has not seen. Every origin
 * answer, flagged or not, is written back under `entity:<id>` for `ttl`
 * seconds, so across a batch job each id reaches the origin at most once per
 * TTL window however many callers ask about it.
 *
 * `fetchFlagged` must THROW on failure rather than return an empty set: an
 * empty set would be memoised as "not flagged" for every id until the TTL
 * expires. Its error propagates to the caller unchanged.
 *
 * A Redis outage degrades to the uncached origin call for the full list.
 */
export async function getFlaggedIdsWithCache({
  entity,
  ids,
  fetchFlagged,
  ttl = FLAGGED_IDS_DEFAULT_TTL_SECONDS,
}: {
  entity: CacheEntities;
  ids: string[];
  fetchFlagged: (missingIds: string[]) => Promise<Set<string>>;
  ttl?: number;
}): Promise<Set<string>> {
  const uniqueIds = Array.from(new Set(ids));
  if (uniqueIds.length === 0) {
    return new Set();
  }

  const redis = getRedisClient();
  const keyOf = (id: string) =>
    scopeKeyToEnvironment(validateCacheKey(generateCacheKey(entity, id)));

  let cachedFlags: CachedFlag[] | null = null;
  try {
    cachedFlags = await redis.mget<CachedFlag[]>(...uniqueIds.map(keyOf));
  } catch (error) {
    console.error(`Redis cache error reading ${entity} flags:`, error);
  }
  if (!cachedFlags) {
    return fetchFlagged(uniqueIds);
  }
  const cached = cachedFlags;

  const flagged = new Set<string>();
  const missing: string[] = [];
  uniqueIds.forEach((id, index) => {
    const flag = cached[index];
    if (flag === true) {
      flagged.add(id);
    } else if (flag !== false) {
      missing.push(id);
    }
  });
  if (missing.length === 0) {
    return flagged;
  }

  const freshlyFlagged = await fetchFlagged(missing);
  freshlyFlagged.forEach((id) => flagged.add(id));

  try {
    const pipeline = redis.pipeline();
    missing.forEach((id) => {
      pipeline.set(keyOf(id), freshlyFlagged.has(id), { ex: ttl });
    });
    await pipeline.exec();
  } catch (error) {
    console.error(`Redis cache error writing ${entity} flags:`, error);
  }

  return flagged;
}
